// src/yomiri/tracking/TrackerManager.ts
import type { TrackerId, TrackerService, TrackRecord } from './types.ts';
import { aniListTracker } from './services/AniListTracker.ts';
import { myAnimeListTracker } from './services/MyAnimeListTracker.ts';
import { delayedTrackingStore } from './DelayedTrackingStore.ts';

class TrackerManager {
    private trackers: Map<TrackerId, TrackerService> = new Map();

    constructor() {
        this.register(aniListTracker);
        this.register(myAnimeListTracker);

        // Escuchar reconexión a Internet para vaciar la cola de seguimiento offline
        if (typeof window !== 'undefined') {
            window.addEventListener('online', () => {
                console.log('[TrackerManager] Dispositivo en línea. Vaciando cola de tracking retrasada...');
                void this.flushDelayedQueue();
            });
            // Intento inicial diferido
            setTimeout(() => void this.flushDelayedQueue(), 4000);
        }
    }

    register(service: TrackerService) {
        this.trackers.set(service.id, service);
    }

    getTrackers(): TrackerService[] {
        return Array.from(this.trackers.values());
    }

    getTracker(id: TrackerId): TrackerService | undefined {
        return this.trackers.get(id);
    }

    async getTracksForManga(mangaId: string): Promise<TrackRecord[]> {
        return delayedTrackingStore.getTrackRecordsForManga(mangaId);
    }

    /**
     * Disparador automático invocado cuando el usuario finaliza o marca como leído un capítulo.
     * Replica exactamente la lógica de TrackChapter.kt de Mihon.
     */
    async onChapterRead(mangaId: string, sourceId: string, chapterNumber: number): Promise<void> {
        if (chapterNumber <= 0 || isNaN(chapterNumber)) return;

        const tracks = await this.getTracksForManga(mangaId);
        if (!tracks.length) return;

        for (const track of tracks) {
            // Solo actualizar si el nuevo capítulo es mayor al registrado
            if (chapterNumber > track.lastChapterRead) {
                const service = this.getTracker(track.trackerId);
                const updatedTrack: TrackRecord = {
                    ...track,
                    lastChapterRead: chapterNumber,
                    // Si alcanzó el total de capítulos, marcar como completado
                    status: (track.totalChapters > 0 && chapterNumber >= track.totalChapters) ? 'COMPLETED' : track.status,
                    updatedAt: Date.now()
                };

                if (service && service.isAuthenticated() && navigator.onLine) {
                    try {
                        await service.updateEntry(updatedTrack, true);
                        console.log(`[TrackerManager] Progreso sincronizado con éxito en ${service.name}: Cap ${chapterNumber}`);
                    } catch (err) {
                        console.warn(`[TrackerManager] Error al sincronizar con ${service.name}. Encolando offline:`, err);
                        await delayedTrackingStore.enqueue({
                            trackerId: track.trackerId,
                            mangaId,
                            remoteId: track.remoteId,
                            lastChapterRead: chapterNumber
                        });
                        await delayedTrackingStore.saveTrackRecord(updatedTrack);
                    }
                } else {
                    // Guardar localmente y encolar
                    await delayedTrackingStore.saveTrackRecord(updatedTrack);
                    await delayedTrackingStore.enqueue({
                        trackerId: track.trackerId,
                        mangaId,
                        remoteId: track.remoteId,
                        lastChapterRead: chapterNumber
                    });
                }
            }
        }
    }

    /**
     * Vacía y procesa todos los elementos pendientes en la cola offline con política de reintentos.
     */
    async flushDelayedQueue(): Promise<void> {
        if (!navigator.onLine) return;
        const queue = await delayedTrackingStore.getQueue();
        if (!queue.length) return;

        console.log(`[TrackerManager] Procesando ${queue.length} elementos pendientes en la cola de tracking`);

        for (const item of queue) {
            const service = this.getTracker(item.trackerId);
            if (!service || !service.isAuthenticated()) continue;

            const tracks = await delayedTrackingStore.getTrackRecordsForManga(item.mangaId);
            const targetTrack = tracks.find(t => t.trackerId === item.trackerId);
            if (!targetTrack) {
                await delayedTrackingStore.dequeue(item.id);
                continue;
            }

            try {
                await service.updateEntry({
                    ...targetTrack,
                    lastChapterRead: item.lastChapterRead
                }, true);
                await delayedTrackingStore.dequeue(item.id);
                console.log(`[TrackerManager] Cola resuelta para manga ${item.mangaId} en ${service.name}`);
            } catch (error) {
                console.warn(`[TrackerManager] Reintento fallido para ${item.id}:`, error);
            }
        }
    }
}

export const trackerManager = new TrackerManager();
